import { useEffect, useState } from "react";
import axios from "axios";
import PageShell from "../../components/public/PageShell";
import Pagination from "../../components/public/Pagination";
import { PlayCircle, X, FilmSlate } from "@phosphor-icons/react";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 9;

// Extract YouTube ID from any share/watch URL. Returns null otherwise.
const parseYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

export default function VideosList() {
  usePageMeta({
    title: "Videos — Bon-Heur Pharmaceuticals",
    description: "Watch inside our labs, lines and clinical stories. A growing library of videos from Bon-Heur Pharmaceuticals.",
    keywords: "Bon-Heur videos, pharma manufacturing videos, R&D videos, laboratory tour",
  });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/public/videos?page=${page}&limit=${PER_PAGE}`)
      .then((r) => {
        const env = r.data || {};
        setItems(Array.isArray(env) ? env : env.items || []);
        setTotal(Array.isArray(env) ? env.length : env.total || 0);
        setPageCount(Array.isArray(env) ? 1 : env.pages || 1);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
        setPageCount(1);
      })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <PageShell
      pageKey="videos"
      eyebrow="Inside our capability"
      title="Videos from the floor"
      subtitle="Manufacturing lines, R&D snapshots and stories from the people who make every product possible."
    >
      <section className="py-14 lg:py-20 bg-white" data-testid="videos-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-video rounded-2xl bg-[#F1F5F9] animate-pulse" />
              ))}
            </div>
          ) : total === 0 ? (
            <div className="py-24 text-center text-[#64748B]" data-testid="videos-empty">
              <FilmSlate size={36} className="mx-auto text-[#CBD5E1] mb-3" />
              <p>No videos yet. Please check back soon.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 text-sm text-[#64748B]">
                Showing <b className="text-[#1F2937]">{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b className="text-[#1F2937]">{total}</b> videos
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="videos-grid">
                {items.map((v) => {
                  const yt = parseYouTubeId(v.youtube_url);
                  const thumb = v.thumbnail_url
                    ? (v.thumbnail_url.startsWith("http") ? v.thumbnail_url : `${BACKEND}${v.thumbnail_url}`)
                    : (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : null);
                  return (
                    <button
                      key={v.id}
                      onClick={() => setOpen(v)}
                      className="group relative aspect-video rounded-2xl overflow-hidden bg-[#0F172A] border border-[#E2E8F0] hover:border-[#C2410C] transition-all duration-500 text-left"
                      data-testid={`video-card-${v.id}`}
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={v.title}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#C2410C]/40 via-[#0F172A] to-[#0EA5E9]/30" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/25 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-16 h-16 rounded-full bg-[#C2410C] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                          <PlayCircle size={30} weight="fill" className="text-white" />
                        </span>
                      </div>
                      <div className="absolute left-5 right-5 bottom-5 text-white">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/70">Watch</div>
                        <div className="mt-1 text-lg font-semibold line-clamp-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{v.title}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                testIdPrefix="videos-pagination"
              />
            </>
          )}
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setOpen(null)}
          data-testid="video-modal"
        >
          <button
            onClick={() => setOpen(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close video"
            data-testid="video-modal-close"
          >
            <X size={22} weight="bold" />
          </button>
          <div className="w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const yt = parseYouTubeId(open.youtube_url);
              if (yt) {
                return (
                  <iframe
                    src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0&modestbranding=1`}
                    title={open.title}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                  />
                );
              }
              // Fallback for a raw file URL (unlikely for this collection but safe)
              const src = open.youtube_url && open.youtube_url.startsWith("http")
                ? open.youtube_url
                : `${BACKEND}${open.youtube_url}`;
              return <video src={src} controls autoPlay className="w-full h-full" />;
            })()}
          </div>
        </div>
      )}
    </PageShell>
  );
}
